import { AnimatePresence } from "framer-motion"
import * as React from "react"
import { Col, Hidden, Row, Visible } from "react-grid-system"
import { SlideOutHeight } from "../atoms/animations/SlideOutHeight"
import { HorizontalSpacer, Spacer } from "../atoms/spacers"
import { theme } from "../theme"
import { useRemoveGroup } from "../../hooks/useRemoveGroup"
import Card from "../atoms/Card"
import CardBar from "../atoms/CardBar"
import Subtitle from "../atoms/typography/Subtitle"
import PrimaryButton from "../atoms/buttons/PrimaryButton"
import SmallButton from "../atoms/buttons/SmallButton"
import { AddIcon } from "../atoms/icons/AddIcon"
import MemberProfileOverview from "../molecules/MemberProfileOverview"
import Modal from "../molecules/Modals/Modal"
import AddTerminalToGroupModal from "../molecules/Modals/AddTerminalToGroupModal"
import InviteMemberToGroupModal from "../molecules/Modals/InviteMemberToGroupModal"
import { Link } from "react-router-dom"
import RemoveMemberCard from "../molecules/RemoveMemberCard"
import TerminalCardWithActions from "../molecules/TerminalCardWithActions"
import { useUser } from "../../hooks/useUser"
import DeleteGroupWarningModalContent from "../molecules/Modals/DeleteGroupWarningModalContent"
import { Member } from "../../utils/models"
import Body from "../atoms/typography/Body"
import { ArrowDropDownIcon } from "../atoms/icons/ArrowDropDownIcon"
import { ApolloError } from "@apollo/client"
import { captureExceptionWithContext } from "../../utils/sentry"

interface Props {
  members: Array<Member>
  open: boolean
  onClickClose: () => void
  groupName: string
  terminals: Array<{
    src: string
    name: string
    serialNumber: string
    id: string | number
  }>
  isEditing?: boolean
  groupId: number | string
  orgId: number | string
  isAdminGroup: boolean
}

const GroupOverview: React.FunctionComponent<Props> = ({
  members,
  open,
  onClickClose,
  groupName,
  terminals,
  isEditing,
  groupId,
  orgId,
  isAdminGroup,
}) => {
  const { user } = useUser()
  const [inviteMemberOpen, setInviteMemberOpen] = React.useState(false)
  const [addTerminalOpen, setAddTerminalOpen] = React.useState(false)
  const [deleteGroupModalOpen, setDeleteGroupModalOpen] = React.useState(false)

  const onCompletedRemoveGroup = () => {
    console.log("Removed group")
  }
  const onErrorRemoveGroup = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, `error removing group ${groupId} from organization ${orgId}`)
  }

  const { removeGroup } = useRemoveGroup({
    onCompleted: onCompletedRemoveGroup,
    onError: onErrorRemoveGroup,
  })

  const onClickRemoveGroup = async () => {
    try {
      await removeGroup({
        variables: {
          organizationGroupId: groupId,
          organizationId: orgId,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, `error removing group ${groupId} from organization ${orgId}`)
    }
  }

  const myMemberInfo = members.find((x) => x.member.email === user?.email)

  return (
    <>
      <Card noPadding>
        <CardBar open={open}>
          <Row justify="around" align="center">
            <Col lg={4} md={7}>
              {isAdminGroup && <Body color={theme.colors.blue}>Admin group</Body>}
              <Subtitle color={theme.colors.white}>{groupName}</Subtitle>
            </Col>
            <Hidden xs md>
              <Col lg={4}>
                <Row justify="around">
                  <Body color={theme.colors.white}>{members.length} members</Body>
                  <Body color={theme.colors.white}>My role: {myMemberInfo?.member?.role}</Body>
                  <Body color={theme.colors.white}>My title: {myMemberInfo?.member?.title}</Body>
                </Row>
              </Col>
            </Hidden>
            <Col lg={4} md={5}>
              <Row justify="end">
                <Hidden xs md>
                  <Link to="/dashboard/groups">
                    <SmallButton title="View on dashboard" />
                  </Link>
                  <Spacer spacing={1} />
                </Hidden>
                <SmallButton
                  title={open ? "Hide" : "Open"}
                  icon={<ArrowDropDownIcon color={theme.colors.greyDark} />}
                  onClick={onClickClose}
                />
              </Row>
            </Col>
          </Row>
        </CardBar>
        <SlideOutHeight isVisible={open}>
          <Visible xs>
            <HorizontalSpacer spacing={2} />
            <Row justify="center">
              <Link to="/dashboard">
                <PrimaryButton title="View on dashboard" />
              </Link>
            </Row>
            <HorizontalSpacer spacing={2} />
          </Visible>
          {isEditing && (
            <>
              <HorizontalSpacer spacing={2} />
              <Row justify="center">
                {!isAdminGroup && (
                  <PrimaryButton title="Delete group" onClick={() => setDeleteGroupModalOpen(true)} destructive />
                )}
              </Row>
            </>
          )}
          <div style={{ padding: theme.spacings.large }}>
            <Subtitle>Members</Subtitle>
            {isEditing && (
              <>
                <HorizontalSpacer spacing={2} />
                <PrimaryButton title="Invite new member" icon={<AddIcon />} onClick={() => setInviteMemberOpen(true)} />
              </>
            )}
          </div>
          <Row gutterWidth={16} style={{ padding: "16px 8px" }}>
            {members.map((member) => (
              <Col md={3} xs={12} key={member.member.id}>
                {isEditing ? (
                  <RemoveMemberCard member={member} orgId={orgId} groupId={groupId} />
                ) : (
                  <MemberProfileOverview
                    imgSrc={member.member?.src ?? ""}
                    name={member.member?.name}
                    role={member.member?.role}
                    title={member.member?.title}
                  />
                )}
              </Col>
            ))}
          </Row>
          <HorizontalSpacer spacing={3} />
          <div style={{ padding: theme.spacings.large }}>
            <Subtitle>Terminals</Subtitle>
            {isEditing && !isAdminGroup && (
              <>
                <HorizontalSpacer spacing={2} />
                <PrimaryButton title="Add new terminal" icon={<AddIcon />} onClick={() => setAddTerminalOpen(true)} />
              </>
            )}
          </div>
          <Row gutterWidth={16} style={{ padding: "16px 8px" }}>
            {terminals.map((t) => (
              <Col md={3} xs={12} key={t.id}>
                <TerminalCardWithActions
                  terminal={t}
                  isEditing={isEditing && !isAdminGroup}
                  orgId={orgId}
                  groupId={groupId}
                />
              </Col>
            ))}
          </Row>
        </SlideOutHeight>
      </Card>

      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {inviteMemberOpen && (
          <Modal open={inviteMemberOpen} onClose={() => setInviteMemberOpen(!inviteMemberOpen)} closeIcon>
            <InviteMemberToGroupModal groupName={groupName} groupId={groupId} orgId={orgId} />
          </Modal>
        )}
      </AnimatePresence>
      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {addTerminalOpen && (
          <Modal open={addTerminalOpen} onClose={() => setAddTerminalOpen(!addTerminalOpen)} closeIcon>
            <AddTerminalToGroupModal groupName={groupName} groupId={groupId} orgId={orgId} />
          </Modal>
        )}
      </AnimatePresence>
      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {deleteGroupModalOpen && (
          <Modal open={deleteGroupModalOpen} onClose={() => setDeleteGroupModalOpen(!deleteGroupModalOpen)} closeIcon>
            <DeleteGroupWarningModalContent
              groupName={groupName}
              onClose={() => setDeleteGroupModalOpen(false)}
              onClickRemoveGroup={onClickRemoveGroup}
            />
          </Modal>
        )}
      </AnimatePresence>
    </>
  )
}

export default GroupOverview
